// Matchlight Stage: the page the box behind the LED wall opens full screen.
// Open once as /stage.html?venue=wensleydale and it follows every match at that venue.
import { activeCards, boardCards, addedTime, fmtClock, matchMs, timeUp, matchSummary, momentShowing, type SideSummary, phaseLabel, totals, videoPlaying, type MatchState, type Side, type Team, type MatchSponsor, type PlayingVideo, type Moment } from '../shared/match';
import { getRules } from '../shared/rules';
import { slotAt, type SponsorSlot } from '../shared/sponsors';
import { DEFAULT_CREDIT } from '../shared/club';
import { connect, loadClub, esc, contrast, param, readableOnBlack, type Club, venueParam } from './net';

const venue = venueParam();
const pixelExact = param('mode') === '1to1';
const stage = document.getElementById('stage') as HTMLDivElement;
const dot = document.getElementById('offline') as HTMLDivElement;

let club: Club | null = null;
let state: MatchState | null = null;
let sIdx = 0, lastRotate = 0, lastKey = '', stripIdx = -1, finalSince = 0;

const link = connect(venue, {
  role: 'viewer',
  onState(s) { state = s; lastKey = ''; tick(); },
  onStatus(s) { dot.hidden = s === 'live'; },
  onClub() { refreshClub(); },               // Studio saved new squads or sponsors
});

const now = () => link.serverNow();
/**
 * The sponsors for the screen now: those ticked for this match in Matchday setup, or every club sponsor
 * between matches (and for matches started before sponsors could be chosen, with their one match day sponsor).
 */
function sponsorList(): SponsorSlot[] {
  if (state?.sponsors) return state.sponsors.map(sp => ({ sp, matchday: !!sp.matchday }));
  const md = state?.matchSponsor ?? null;
  const others = (club?.sponsors ?? []).filter(x => !md || x.name !== md.name).map(sp => ({ sp: sp as MatchSponsor, matchday: false }));
  return md ? [{ sp: md, matchday: true }, ...others] : others;
}
/** "Scoreboard by Matchlight": on unless the owner has switched it off (it isn't in Studio or Matchday setup). */
const credit = () => club?.credit ?? DEFAULT_CREDIT;
const CREDIT_SLOT: SponsorSlot = { sp: { name: 'Matchlight', logo: '', bg: '#0b1220' }, matchday: false, credit: true };
/** Which sponsor is up now: match day sponsors take every other slot, the rest take turns in between, and Matchlight now and then. */
const sponsor = () => slotAt(sponsorList(), sIdx, credit().on ? CREDIT_SLOT : null);

/** Matchlight's mark as on the app's pages: the dot-matrix M, and MATCH in white with LIGHT in gold. */
const ML_DOTS = [[16, 18], [16, 28], [16, 38], [16, 48], [24, 24], [32, 32], [40, 24], [48, 18], [48, 28], [48, 38], [48, 48]];
const mlMark = () => `<svg class="ml-dots" viewBox="0 0 64 64" aria-hidden="true"><rect width="64" height="64" rx="14" fill="#0b1220" stroke="#26324a" stroke-width="2"/><g fill="#f5b400">${ML_DOTS.map(([x, y]) => `<circle cx="${x}" cy="${y}" r="4"/>`).join('')}</g></svg>`;
const mlWord = () => `<span class="ml-word">Match<b>light</b></span>`;
const creditStrip = () => `<div class="strip ml-strip">${mlMark()}${mlWord()}<span class="ml-tag">${esc(credit().tagline)}<b>matchlight.uk</b></span></div>`;
const creditFull = (s: MatchState) => `<div class="full ml-full"><div class="ml-by">This scoreboard is run by</div>
  <div class="ml-lockup">${mlMark()}${mlWord()}</div><div class="ml-tag">${esc(credit().tagline)}</div><div class="ml-url">matchlight.uk</div></div>${bug(s)}`;
const playerOf = (id: string | null) => {
  if (!id || !club) return null;
  for (const sq of Object.values(club.squads)) {
    const p = sq.players.find(x => x.id === id);
    // today's shirt number when the team sheet has one
    if (p) { const today = state?.lineups?.home.find(x => x.player === id)?.number; return today ? { ...p, number: today } : p; }
  }
  return null;
};

function crest(t: Team) {
  if (t.crest) return `<img src="${esc(t.crest)}" alt="">`;
  const fg = contrast(t.color), initial = esc(t.name.charAt(0).toUpperCase());
  return `<svg viewBox="0 0 100 100" aria-hidden="true"><path d="M50 4 L92 18 V52 C92 76 72 90 50 97 C28 90 8 76 8 52 V18 Z" fill="${esc(t.color)}" stroke="#fff" stroke-width="5"/><text x="50" y="66" text-anchor="middle" font-family="Big Shoulders Display, Impact, sans-serif" font-weight="900" font-size="50" fill="${fg}">${initial}</text></svg>`;
}

function strip() {
  const now = sponsor();
  if (!now) return '';
  if (now.credit) return creditStrip();
  const { sp, matchday } = now;
  return `<div class="strip" style="background:${esc(sp.bg)};color:${contrast(sp.bg)}">${matchday ? `<span class="mdlabel">Today's match day<br>sponsor</span>` : ''}<img src="${esc(sp.logo)}" alt=""><strong>${esc(sp.name)}</strong></div>`;
}

/** "+5" for rugby; nothing in football, where every goal is one. */
const plus = (s: MatchState, pts: number) => getRules(s.rulesId).scoring.every(x => x.pts === 1) ? '' : `+${pts}`;
const addedBadge = (s: MatchState, cls = 'added') => { const a = addedTime(s); return a ? `<span class="${cls}">+${a}</span>` : ''; };

const bug = (s: MatchState) => { const t = totals(s); return `<div class="bug"><i style="background:${esc(s.teams.home.color)}"></i>${esc(s.teams.home.short)} ${t.home}–${t.away} ${esc(s.teams.away.short)}<i style="background:${esc(s.teams.away.color)}"></i>${s.phase === 'pre' ? '' : s.phase === 'FT' ? '<span class="t">FT</span>' : `<span class="t ${timeUp(s, now()) ? 'over' : ''}" data-clock>${fmtClock(matchMs(s, now()))}</span>${addedBadge(s, 'badd')}`}</div>`; };

function binsHTML(s: MatchState, side: Side) {
  // red cards (football) stay by the team for the rest of the match; sin-bin timers count down beside them
  const reds = boardCards(s, side).map(c => `<span class="redc">${c.number ? esc(c.number) : ''}</span>`).join('');
  return reds + activeCards(s, now()).filter(c => c.team === side)
    .map(c => `<span class="bin ${c.remainingMs <= 0 ? 'done' : ''}">${c.number ? `#${esc(c.number)} ` : ''}${fmtClock(c.remainingMs)}</span>`).join('');
}

function idle() {
  return `<div class="idle"><div class="idle-crest">${club ? `<img src="${esc(club.crest)}" alt="">` : ''}</div>
    <div class="idle-name">${esc(club?.name ?? '')}</div></div>${strip()}`;
}

function board(s: MatchState) {
  const t = totals(s), clock = s.phase === 'pre' ? '' : fmtClock(matchMs(s, now()));
  return `<div class="board">
    <div class="crest home">${crest(s.teams.home)}</div><div class="crest away">${crest(s.teams.away)}</div>
    <div class="tname home" style="color:${readableOnBlack(s.teams.home.color)}">${esc(s.teams.home.name)}</div>
    <div class="tname away" style="color:${readableOnBlack(s.teams.away.color)}">${esc(s.teams.away.name)}</div>
    <div class="score home">${t.home}</div><div class="score away">${t.away}</div>
    <div class="mid">${clock ? `<div class="clock ${s.clock.running ? '' : 'stopped'} ${timeUp(s, now()) ? 'over' : ''}" data-clock>${clock}</div>` : '<div class="clock ko">KO</div>'}${addedBadge(s)}<div class="period">${esc(phaseLabel(s))}</div></div>
    <div class="bins home" data-bins="home">${binsHTML(s, 'home')}</div><div class="bins away" data-bins="away">${binsHTML(s, 'away')}</div>
  </div>${strip()}`;
}

/** Shrink a long word (CONVERSION, PENALTY TRY) to fit the screen's width; again once the display font has loaded. */
function fitWord() {
  const fit = () => {
    const w = stage.querySelector<HTMLElement>('.fword');
    if (!w) return;
    w.style.fontSize = '';
    const room = 680, wide = (w.firstElementChild as HTMLElement | null)?.offsetWidth ?? 0;   // the word itself, not its full-width box
    if (wide > room) w.style.fontSize = `${Math.floor(parseFloat(getComputedStyle(w).fontSize) * room / wide)}px`;
  };
  fit();
  document.fonts?.ready.then(fit).catch(() => { /* fonts never load: keep the first fit */ });
}

function celebration(s: MatchState, m: Moment) {
  const t = totals(s);
  if (m.next) t[m.next.team] -= m.next.pts;   // the try's screen keeps the try's score: the conversion's own screen adds it
  const found = m.team === 'home' ? playerOf(m.player) : null;
  const p = found && found.photo && found.showPhoto !== false ? found : null;   // no photo on screen without consent
  if (p) {
    const name = p.name || s.teams.home.name;
    // the club's own colour for the word and the band (Wensleydale's gold looks exactly as it always has)
    const band = s.teams.home.color;
    return `<div class="celeb"><div class="hoop" style="background:${esc(band)}"></div><div class="hoop2" style="background:${esc(band)}"></div>
      <div class="word" style="color:${readableOnBlack(band)}">${esc(m.label.toUpperCase())}</div>
      <div class="who">${esc(name)}</div><div class="pts">${[plus(s, m.pts), p.number ? `No. ${esc(p.number)}` : ''].filter(Boolean).join(' · ')}</div>
      <div class="line" style="color:${contrast(band)}">${t.home} – ${t.away}</div><img class="player" src="${esc(p.photo)}" alt=""></div>`;
  }
  const team = s.teams[m.team];
  if (!getRules(s.rulesId).scoring.find(x => x.kind === m.kind)?.photo) {
    // conversions, penalties, drop goals and penalty tries: the try screen's look, in the scoring side's colours
    const band = team.color, ink = contrast(band);
    return `<div class="celeb flash"><div class="hoop" style="background:${esc(band)}"></div><div class="hoop2" style="background:${esc(band)}"></div>
      <div class="fword" style="color:${readableOnBlack(band)}"><span>${esc(m.label.toUpperCase())}</span></div>
      <div class="fwho">${esc(team.name)} ${plus(s, m.pts)}</div>
      <div class="fline" style="color:${ink}">${esc(s.teams.home.short)} ${t.home} – ${t.away} ${esc(s.teams.away.short)}</div></div>`;
  }
  const col = team.color, who = found?.name || team.name;
  return `<div class="celebrate" style="background:${esc(col)};color:${contrast(col)}">
    <div class="cword">${esc(m.label.toUpperCase())}</div><div class="cwho">${esc(who)} ${plus(s, m.pts)}</div>
    <div class="cline">${esc(s.teams.home.short)} ${t.home} – ${t.away} ${esc(s.teams.away.short)}</div></div>`;
}

const PLURAL: Record<string, string> = { Try: 'tries', Conversion: 'conversions', Penalty: 'penalties', 'Drop goal': 'drop goals', 'Penalty try': 'penalty tries', Goal: 'goals', 'Own goal': 'own goals' };
const scoreWord = (label: string, n: number) => `${n}\u00a0${n === 1 ? label.toLowerCase() : PLURAL[label] ?? `${label.toLowerCase()}s`}`;

/** One side's half of the full-time summary: what they scored, who scored the tries, any bonus point. */
function sideSummary(s: MatchState, team: Team, sum: SideSummary) {
  const rules = getRules(s.rulesId);
  const scored = sum.counts.length ? sum.counts.map(c => scoreWord(c.label, c.n)).join(' · ') : 'No score';
  // rugby: "Smith 2, Jones"; football: "Smith 23', 67', Own goal 34'"
  const names = sum.scorers.map(x => {
    const name = x.player == null ? 'Own goal' : playerOf(x.player)?.name;
    if (!name) return '';
    return rules.scorerMinutes ? `${name} ${x.mins.join(', ')}` : `${name}${x.tries > 1 ? ` ${x.tries}` : ''}`;
  }).filter(Boolean);
  const bonus = sum.bonus.map(b => b === 'try' ? 'Try bonus point' : 'Losing bonus point');
  return `<div class="ft-side"><div class="ft-team" style="color:${readableOnBlack(team.color)}">${esc(team.name)}</div>
    <div class="ft-line">${esc(scored)}</div>
    ${names.length ? `<div class="ft-line ft-scorers">${esc(rules.words.scores.replace(/^./, c => c.toUpperCase()))}: ${names.map(n => `<span class="nw">${esc(n)}</span>`).join(rules.scorerMinutes ? ' · ' : ', ')}</div>` : ''}
    ${bonus.map(b => `<span class="ft-bp">${esc(b)}</span>`).join(' ')}</div>`;
}

/** Full time: the result, the final score and a summary. A home win gets the club's colours, light rays and confetti. */
function finalView(s: MatchState) {
  const sum = matchSummary(s), { home, away } = s.teams;
  const win = sum.result === 'home';
  const words = sum.result === 'draw' ? 'Draw' : `${(win ? home : away).name} win`;
  const band = home.color, accent = readableOnBlack(band);
  // confetti in the club's colour and white; positions are fixed so the screen looks the same on every redraw
  const confetti = win ? Array.from({ length: 34 }, (_, i) => {
    const x = (i * 211) % 720, d = 3.6 + ((i * 37) % 30) / 10, delay = -((i * 53) % 60) / 10, w = 8 + (i % 3) * 3;
    const col = i % 3 === 0 ? '#ffffff' : i % 3 === 1 ? band : accent;
    return `<i style="left:${x}px;width:${w}px;background:${col};animation-duration:${d}s;animation-delay:${delay}s"></i>`;
  }).join('') : '';
  return `<div class="ft ${win ? 'win' : sum.result === 'draw' ? 'draw' : 'loss'}">
    ${win ? `<div class="ft-rays" style="--club:${esc(band)}"></div><div class="ft-confetti">${confetti}</div>` : ''}
    <div class="ft-head" style="color:${win ? accent : 'var(--muted-ft)'}">Full time</div>
    <div class="ft-result fword" style="color:${win ? accent : '#e9edf2'}"><span>${esc(words)}</span></div>
    <div class="ft-score"><div class="ft-crest">${crest(home)}</div><div class="ft-num">${sum.home.points}</div><div class="ft-dash">–</div><div class="ft-num">${sum.away.points}</div><div class="ft-crest">${crest(away)}</div></div>
    <div class="ft-sides">${sideSummary(s, home, sum.home)}${sideSummary(s, away, sum.away)}</div>
  </div>${strip()}`;
}

/** The match day sponsors ticked in Matchday setup (or the one chosen in older matches). */
function matchdaySponsors(s: MatchState): MatchSponsor[] {
  if (s.sponsors) return s.sponsors.filter(x => x.matchday);
  return s.matchSponsor ? [s.matchSponsor] : [];
}

/** After the result: thank today's match day sponsors, logos on their own colours. */
function thanksView(s: MatchState) {
  const md = matchdaySponsors(s);
  return `<div class="ty"><div class="ty-head">Special thanks to today's<br>match day sponsor${md.length > 1 ? 's' : ''}</div>
    <div class="ty-tiles ${md.length > 3 ? 'many' : ''}">${md.map(sp => `<div class="ty-tile"><div class="ty-logo" style="background:${esc(sp.bg)}"><img src="${esc(sp.logo)}" alt=""></div><div class="ty-name">${esc(sp.name)}</div></div>`).join('')}</div>
    ${bug(s)}</div>`;
}

/** Today's team for one side: starters in two columns (rugby 1–8 and 9–15), replacements or substitutes underneath. */
function teamsView(s: MatchState, side: Side) {
  const team = s.teams[side], rules = getRules(s.rulesId), xv = rules.starters, split = xv === 15 ? 8 : Math.ceil(xv / 2);
  const rows = side === 'home'
    ? (s.lineups?.home ?? []).map(t => ({ number: t.number, name: playerOf(t.player)?.name || (t.number ? `No. ${t.number}` : 'Player') }))
    : (s.lineups?.away ?? []);
  const reps = rows.filter(r => Number(r.number) > xv), starters = rows.filter(r => !(Number(r.number) > xv));
  const numbered = starters.every(r => r.number);
  const left = numbered ? starters.filter(r => Number(r.number) <= split) : starters.slice(0, Math.ceil(starters.length / 2));
  const right = starters.filter(r => !left.includes(r));
  const li = (r: { number: string; name: string }) => `<li><b>${esc(r.number)}</b><span>${esc(r.name)}</span></li>`;
  // number boxes, name and bench numbers all in this team's colour (brightened if it would vanish on the dark screen)
  const accent = readableOnBlack(team.color);
  return `<div class="tm" style="--tm-accent:${accent};--tm-ink:${contrast(accent)}"><div class="tm-head"><div class="tm-crest">${crest(team)}</div>
      <div><div class="tm-name" style="color:var(--tm-accent)">${esc(team.name)}</div><div class="tm-sub">Today's team</div></div></div>
    <div class="tm-cols"><ol>${left.map(li).join('')}</ol><ol>${right.map(li).join('')}</ol></div>
    ${reps.length ? `<div class="tm-reps"><span>${esc(rules.words.bench)}</span>${reps.map(r => `<b>${esc(r.number)}</b> ${esc(r.name)}`).join('<i>·</i>')}</div>` : ''}
  </div>${strip()}`;
}

function sponsorsView(s: MatchState) {
  const now = sponsor();
  if (now?.credit) return creditFull(s);
  const sp = now?.sp;
  return `<div class="full ${now?.matchday ? 'matchday' : ''}" style="background:${esc(sp?.bg ?? '#000')};color:${contrast(sp?.bg ?? '#000000')}">
    ${now?.matchday ? `<div class="mdhead">Today's match day sponsor</div>` : ''}${sp ? `<img src="${esc(sp.logo)}" alt="">` : ''}</div>${bug(s)}`;
}

function videoView(s: MatchState, v: PlayingVideo) {
  return `<div class="vidwrap"><video src="${esc(v.url)}" playsinline ${v.loop ? 'loop' : ''}></video></div>${bug(s)}`;
}

/** Start the promo video at the right point: a screen that joins late picks up where everyone else is. */
function startVideo(v: PlayingVideo) {
  const el = stage.querySelector('video');
  if (!el) return;
  const offset = Math.max(0, (now() - v.startedAt) / 1000);
  el.addEventListener('loadedmetadata', () => {
    if (offset > 0.5 && Number.isFinite(el.duration)) el.currentTime = v.loop ? offset % el.duration : Math.min(offset, Math.max(0, el.duration - 0.1));
  }, { once: true });
  el.addEventListener('ended', () => { lastKey = ''; tick(); });
  // sound plays when the kiosk allows it; otherwise play muted rather than not at all
  const tryPlay = () => { try { return Promise.resolve(el.play()); } catch (e) { return Promise.reject(e); } };
  tryPlay().catch(() => { el.muted = true; tryPlay().catch(() => {}); });
}

function tick() {
  const n = now(), s = state;
  if (n - lastRotate > 8000) { sIdx++; lastRotate = n; }   // views that show sponsors include sIdx in their key, so they redraw; full time only swaps its strip
  const showing = s ? momentShowing(s, n) : null, momentOn = !!showing;
  const video = s ? videoPlaying(s, n) : null;
  const shown = !s ? 'idle' : s.view === 'off' ? 'off' : momentOn ? 'moment' : video ? 'video' : s.view === 'video' ? 'board' : s.view;
  let mode: string = shown === 'board' && s?.phase === 'FT' ? 'final' : shown, side: Side = 'home';
  // full time: the result for 30 seconds, then 15 seconds thanking the match day sponsors, and round again
  if (mode === 'final') {
    if (!finalSince) finalSince = n;
    if (matchdaySponsors(s!).length && (n - finalSince) % 45_000 >= 30_000) mode = 'thanks';
  } else finalSince = 0;
  // today's teams: on request, or by themselves before kick-off, taking turns with the scoreboard
  const sides = s ? (['home', 'away'] as Side[]).filter(x => (s.lineups?.[x].length ?? 0) > 0) : [];
  if (mode === 'teams') {
    if (!sides.length) mode = 'board';
    else side = sides[Math.floor(n / 15_000) % sides.length];
  } else if (mode === 'board' && s?.phase === 'pre' && sides.length) {
    const k = Math.floor(n / 12_000) % (sides.length + 1);
    if (k > 0) { mode = 'teams'; side = sides[k - 1]; }
  }
  // never restart a celebration or a video mid-way
  const key = mode === 'moment' ? `moment|${s?.seq}|${showing!.eventId}` : mode === 'video' || mode === 'final' || mode === 'thanks' ? `${mode}|${s?.seq}` : `${mode}|${side}|${s?.seq}|${sIdx}`;
  if (key !== lastKey) {
    lastKey = key;
    stage.innerHTML = mode === 'idle' ? idle() : mode === 'off' ? '' : mode === 'moment' ? celebration(s!, showing!) : mode === 'video' ? videoView(s!, video!)
      : mode === 'sponsors' ? sponsorsView(s!) : mode === 'final' ? finalView(s!) : mode === 'thanks' ? thanksView(s!)
      : mode === 'teams' ? teamsView(s!, side) : board(s!);
    stripIdx = sIdx;
    if (mode === 'video') startVideo(video!);
    stage.classList.toggle('pop', mode === 'moment');
    fitWord();
  } else if (mode === 'final' && stripIdx !== sIdx) {
    // full time: only the sponsor strip moves on, so the celebration keeps playing
    stripIdx = sIdx;
    const st = stage.querySelector('.strip'), next = strip();
    if (st && next) st.outerHTML = next;
  } else if (s) {
    // between changes only the running numbers move
    const c = stage.querySelector('[data-clock]');
    if (c) { c.textContent = fmtClock(matchMs(s, n)); c.classList.toggle('stopped', !s.clock.running); c.classList.toggle('over', timeUp(s, n)); }
    for (const side of ['home', 'away'] as Side[]) { const b = stage.querySelector(`[data-bins="${side}"]`); if (b) b.innerHTML = binsHTML(s, side); }
  }
}

// On the Stage box the screen fills the window; in a preview it fills whatever box it sits in.
const embedded = stage.parentElement !== document.body;

function layout() {
  const w = club?.screen.width ?? 720, h = club?.screen.height ?? 480;
  stage.style.width = `${w}px`; stage.style.height = `${h}px`;
  if (pixelExact && !embedded) { stage.style.transform = 'none'; return; }
  const W = embedded ? stage.parentElement!.clientWidth : innerWidth, H = embedded ? stage.parentElement!.clientHeight : innerHeight;
  const sc = Math.min(W / w, H / h);
  stage.style.transform = `translate(${(W - w * sc) / 2}px, ${(H - h * sc) / 2}px) scale(${sc})`;
}

if (embedded) new ResizeObserver(layout).observe(stage.parentElement!);
else {
  addEventListener('resize', layout);
  // a tap or key press goes full screen (the kiosk launcher normally starts it full screen anyway)
  addEventListener('click', () => document.documentElement.requestFullscreen?.().catch(() => {}));
  addEventListener('keydown', e => { if (e.key === 'f') document.documentElement.requestFullscreen?.().catch(() => {}); });
}
(async () => { try { await (navigator as any).wakeLock?.request('screen'); } catch { /* not offered */ } })();

function refreshClub() {
  return loadClub(venue).then(c => { club = c; document.title = `${c.name} · Stage`; layout(); lastKey = ''; tick(); });
}
refreshClub().catch(() => { stage.innerHTML = `<div class="idle"><div class="idle-name">No club called “${esc(venue)}”</div></div>`; });
layout();
setInterval(tick, 250);
